import { Disposable } from "@baseline/common/disposable";
import React, { FC, ReactNode } from "react";
import { useController } from "../react";


export function Components<T = any>({ items, provider }: Components.Props<T>): ReactNode
{
    const controller = useController(() => new Components.Controller<T>(provider));
    const instances = controller.getInstances(items);

    return (
        React.createElement(
            React.Fragment,
            {},
            instances?.map(
                ({ key, component }) =>
                    React.createElement(component, { key })
            )
        )
    );
}

export namespace Components
{
    export interface Props<T = any>
    {
        items: T[];
        provider: ComponentProvider<T>;
    }

    export class Controller<T = any> implements Disposable
    {
        constructor(
            readonly provider: ComponentProvider<T>
        )
        {
        }

        dispose(): void
        {
        }

        getInstances(items: T[]): Instance<T>[]
        {
            const set = new Set(items);
            const removed =
                Array.from(this.instances.keys())
                    .filter(item => !set.has(item));
            removed.forEach(item => this.instances.delete(item));

            const instances =
                items.map(
                    item =>
                        this.instances.get(item) ??
                        this.createInstance(item)
                );

            return instances;
        }

        private createInstance(item: T): Instance<T>
        {
            const key = this.nextKey++;
            const component = this.provider(item);
            const instance = { key, item, component };
            this.instances.set(item, instance);
            return instance;
        }

        private instances = new Map<T, Instance<T>>();
        private nextKey: number = 1;
    }


    interface Instance<T>
    {
        key: number;
        item: T;
        component: FC;
    }
}


export type ComponentProvider<T = any> = (item: T) => FC;
